import { Code, ConnectError } from '@connectrpc/connect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { proposalProjectApiConnect } from '../lib/api/proposalProjectApi.connect';
import { readBatchId, subscribeToBatchIdChanges, writeBatchId } from '../lib/batchIdStorage';
import { useProposalProject } from './useProposalProject';
/**
 * Returns true when the error means "the batch (or project) doesn't exist
 * server-side", so React Query can stop retrying cleanly. Connect throws
 * ConnectError(Code.NotFound); matching on the code enum rather than a "404"
 * substring keeps this robust against message-text changes.
 */ function isNotFoundError(err) {
    return err instanceof ConnectError && err.code === Code.NotFound;
}
function getLatch(store, batchId) {
    let latch = store.get(batchId);
    if (!latch) {
        latch = {
            terminal: false,
            knownComplete: false,
            snapshot: null,
            error: null
        };
        store.set(batchId, latch);
    }
    return latch;
}
function buildSnapshot(raw) {
    const trackComplete = Boolean(raw.progress?.phaseStatus?.track ?? raw.progress?.details);
    const preparationOnly = raw.status === 'completed' && raw.projectAnalysisStatus === 'not_required';
    const prepared = preparationOnly && raw.totalDocuments > 0 && raw.processedDocuments === raw.totalDocuments && raw.failedDocuments === 0;
    const isFailed = raw.status === 'failed' || raw.projectAnalysisStatus === 'failed' || preparationOnly && !prepared;
    const isFullyComplete = !isFailed && raw.status === 'completed' && (trackComplete || prepared);
    const isActive = !isFullyComplete && !isFailed;
    return {
        batchId: raw.batchId,
        status: raw.status,
        percentage: raw.progress?.percentage ?? 0,
        currentStep: raw.progress?.currentStep ?? '',
        documentsProcessed: raw.progress?.documentsProcessed ?? 0,
        totalDocuments: raw.progress?.totalDocuments ?? 0,
        projectAnalysisStatus: raw.projectAnalysisStatus,
        projectAnalysisPercentage: raw.projectAnalysisProgress?.percentage,
        documents: (raw.documents ?? []).map((d)=>({
                attachmentId: d.attachmentId,
                filename: d.filename,
                status: d.status,
                documentType: d.documentType
            })),
        details: raw.progress?.details,
        isFullyComplete,
        isFailed,
        isActive
    };
}
const POLL_INTERVAL_MS = 2000;
const STATUS_STALE_MS = 1500;
export function useProposalProgress(projectId) {
    // The server only populates `proposalData.batchProcessing` while the batch is
    // genuinely active (processing/analyzing/pending sync). After completion the
    // field disappears. `ProjectFilesSection` works around this by caching the
    // last-known batchId in sessionStorage under `batch:${projectId}`; we read
    // the same key so the bar can show a completion state after the server has
    // dropped the field, and survive page refreshes mid-batch.
    //
    // Note: we deliberately never clear this key on completion. sessionStorage is
    // tab-scoped, and keeping the batchId lets a post-completion tab refresh
    // re-hydrate the bar so the user sees the "Analysis complete" state before
    // auto-hide (autoHide handles the visual lifecycle from there). The key
    // naturally clears when the tab closes.
    const { data: projectData } = useProposalProject(projectId);
    const serverBatchId = projectData?.proposalData?.batchProcessing?.batchId ?? null;
    const [storedBatchId, setStoredBatchId] = useState(()=>readBatchId(projectId));
    // Re-read on projectId switch (navigating between proposals).
    useEffect(()=>{
        setStoredBatchId(readBatchId(projectId));
    }, [
        projectId
    ]);
    // Cross-component sync: ProjectFilesSection calls writeBatchId() when a new
    // batch starts, which dispatches a custom event we subscribe to here. No
    // polling required.
    useEffect(()=>{
        if (!projectId) return;
        return subscribeToBatchIdChanges(projectId, (next)=>{
            setStoredBatchId((prev)=>prev === next ? prev : next);
        });
    }, [
        projectId
    ]);
    // Mirror server-discovered batchIds into sessionStorage so a refresh
    // mid-batch still hydrates the bar without waiting for the next poll.
    useEffect(()=>{
        if (!projectId) return;
        if (serverBatchId && serverBatchId !== storedBatchId) {
            writeBatchId(projectId, serverBatchId);
        }
    }, [
        projectId,
        serverBatchId,
        storedBatchId
    ]);
    const batchId = serverBatchId ?? storedBatchId;
    const enabled = Boolean(projectId && batchId);
    // The batch id came only from sessionStorage, with the server reporting no
    // active batch for this project — per the header comment above, that only
    // happens once the batch has already finished, so a NotFound for it is
    // expected rather than a real failure. Requiring `projectData` to be
    // defined matters on first render: while useProposalProject is still
    // loading, its data is undefined too, and that's "the server hasn't told
    // us anything yet", not "the server told us there's no active batch" — a
    // 404 landing in that window for a batch that's genuinely still active
    // must still surface as an error.
    const knownCompleteFromStorage = Boolean(batchId) && projectData !== undefined && !serverBatchId;
    // Per-batch-id terminal latch — see BatchLatch. Lives outside React
    // Query's cache so it survives cache eviction and re-renders; a batch id
    // that has latched terminal never issues another request.
    const latchStore = useRef(new Map()).current;
    const { data: raw, isLoading, error } = useQuery({
        queryKey: [
            'batchStatus',
            projectId,
            batchId
        ],
        queryFn: async ()=>{
            if (!projectId || !batchId) throw new Error('Missing projectId/batchId');
            return proposalProjectApiConnect.getBatchStatus(projectId, batchId);
        },
        enabled,
        staleTime: STATUS_STALE_MS,
        // A hidden tab pauses, as the global host does (SAD-993, SAD-997): every
        // open tab polled every 2s. On return the interval resumes, so the bar
        // catches up (and completion fires) on its next tick.
        refetchIntervalInBackground: false,
        retry: (failureCount, err)=>{
            if (isNotFoundError(err)) return false;
            return failureCount < 3;
        },
        refetchInterval: (query)=>{
            if (!batchId) return false;
            // Read-only: the latch is written by the snapshot/error memo below.
            // Consulting it here (rather than only `query.state`) is what keeps a
            // batch id whose cached data has since been evicted from resuming.
            if (latchStore.get(batchId)?.terminal) return false;
            const data = query.state.data;
            if (data) {
                const built = buildSnapshot(data);
                if (built.isFullyComplete || built.isFailed) return false;
            }
            if (query.state.error) return false;
            return POLL_INTERVAL_MS;
        }
    });
    const { snapshot, effectiveError } = useMemo(()=>{
        if (!batchId) return {
            snapshot: null,
            effectiveError: null
        };
        const latch = getLatch(latchStore, batchId);
        if (latch.terminal) {
            return {
                snapshot: latch.snapshot,
                effectiveError: latch.error
            };
        }
        if (error) {
            if (isNotFoundError(error)) {
                // Known complete either because we've already seen this batch id
                // reach a terminal status, or because it's storage-only with no
                // server counterpart (see knownCompleteFromStorage above) — either
                // way the 404 is expected, not an error to surface.
                latch.knownComplete = latch.knownComplete || knownCompleteFromStorage;
                latch.error = latch.knownComplete ? null : error;
            } else {
                latch.error = error;
            }
            latch.terminal = true;
            return {
                snapshot: latch.snapshot,
                effectiveError: latch.error
            };
        }
        if (!raw) return {
            snapshot: null,
            effectiveError: null
        };
        const built = buildSnapshot(raw);
        if (built.isFullyComplete || built.isFailed) {
            latch.terminal = true;
            latch.knownComplete = true;
            latch.snapshot = built;
        }
        return {
            snapshot: built,
            effectiveError: null
        };
    }, [
        raw,
        error,
        batchId,
        knownCompleteFromStorage,
        latchStore
    ]);
    // Route-independent cache invalidation on the completion rising edge.
    // The header bar provider is mounted across every /proposal/:id/* page
    // so this fires regardless of where the user is when the batch finishes,
    // keeping sibling caches (compliance / CLINs / LCATs / outline) fresh.
    // This used to be duplicated in ProjectFilesSection.handleBatchComplete;
    // that copy was removed to avoid the two lists drifting.
    const queryClient = useQueryClient();
    const wasFullyComplete = useRef(false);
    useEffect(()=>{
        const isFullyComplete = snapshot?.isFullyComplete ?? false;
        if (isFullyComplete && !wasFullyComplete.current && projectId) {
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'clins',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'lcats',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'complianceMatrix',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'complianceCoverage',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'shredView',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId
                ]
            });
        }
        wasFullyComplete.current = isFullyComplete;
    }, [
        snapshot?.isFullyComplete,
        projectId,
        queryClient
    ]);
    // Memoise on the primitive fields, not the assembled object. Returning a
    // fresh object literal each render would force every context consumer to
    // re-render on every parent render — the context's own useMemo can't help
    // because its dep array contains the always-new object.
    return useMemo(()=>({
            projectId: projectId ?? null,
            batchId,
            snapshot,
            isLoading,
            error: effectiveError
        }), [
        projectId,
        batchId,
        snapshot,
        isLoading,
        effectiveError
    ]);
}
